import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const feteDuCitronMentonEn: ChauffeurPage = {
  slug: "fete-du-citron-menton",
  title: "Private chauffeur Menton Lemon Festival – VTC",
  subtitle:
    "Private chauffeur during the Menton Lemon Festival (Fête du Citron): access to the Biovès gardens and the parades, transfers from Nice airport and Monaco.",
  keywords: [
    "menton lemon festival chauffeur",
    "fête du citron chauffeur",
    "menton private driver february",
  ],
  hero: {
    title: "Private chauffeur during the Menton Lemon Festival",
    subtitle:
      "Biovès gardens and seafront parades: a dedicated chauffeur when Menton fills up in February and the streets close for the corsos.",
    keywords: [
      "Menton Lemon Festival chauffeur",
      "Biovès gardens VTC",
      "Nice airport Menton transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Menton Lemon Festival",
    badges: ["Airport transfer", "Parade access", "From Monaco"],
  },
  benefits: {
    heading: "Why a chauffeur during the Lemon Festival",
    items: [
      {
        title: "Parades and street closures handled",
        description:
          "The corsos close Menton's seafront and centre. The chauffeur knows the authorised drop-offs nearest the Biovès gardens.",
      },
      {
        title: "Nice airport & Monaco",
        description:
          "Nice Côte d'Azur is around thirty kilometres away, Monaco ten minutes. Name-board welcome, flight tracking and a direct transfer.",
      },
      {
        title: "A comfortable winter event",
        description:
          "In February, a chauffeur spares you the wait outside and the connections: a warm ride between the hotel, the gardens and the parades.",
      },
      {
        title: "5.0/5 from 38 Google reviews",
        description:
          "Punctuality, discretion and immaculate vehicles: the consistency that earns KS Rent Services a 5.0/5 rating from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Lemon Festival",
    services: [
      {
        title: "Airport & Monaco transfer",
        text: "Nice or Monaco to Menton with flight tracking during the parade weekends.",
      },
      {
        title: "Biovès gardens drop-off",
        text: "Approach as close as possible to the citrus display and the stands, within the street closures.",
      },
      {
        title: "Parade access",
        text: "Drop-off and pick-up around the seafront for the golden-fruit corsos and the night parades.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur to chain the gardens, old town and lunch on the sea or Italian side.",
      },
      {
        title: "Family & group shuttles",
        text: "Multi-vehicle coordination for families, groups and guests.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Lemon Festival",
    items: [
      {
        name: "Biovès gardens",
        description:
          "Giant citrus sculptures, the heart of the Lemon Festival.",
      },
      {
        name: "Promenade du Soleil",
        description: "Seafront of the corsos and the float parades.",
      },
      {
        name: "Old town & port of Menton",
        description: "Lanes, basilica and port for lunch or a stroll.",
      },
      {
        name: "Monaco",
        description:
          "Around ten kilometres away, hotels and station for arrivals.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Around thirty kilometres away, Terminals 1 and 2.",
      },
    ],
  },
  generic: {
    heading: "The Menton Lemon Festival from a chauffeur's seat",
    subheading:
      "Every winter in February, Menton celebrates citrus with its sculptures in the Biovès gardens and its parades on the seafront.",
    paragraphs: [
      "Every February, the Menton Lemon Festival (Fête du Citron) draws hundreds of thousands of visitors. The Biovès gardens host the citrus sculptures and the corsos parade along the Promenade du Soleil: the streets close and parking becomes scarce, especially at weekends.",
      "Menton is around ten kilometres from Monaco and thirty from Nice Côte d'Azur airport. With flight tracking and a name-board welcome, the transfer to your hotel runs without waiting, even at peak times.",
      "In winter, chaining the gardens, the parades and lunch without long walks in the cold changes everything. Hourly hire keeps the same chauffeur all day and drops you as close as possible at every stage.",
    ],
    bullets: [
      "Biovès gardens drop-off",
      "Monaco ~10 min away",
      "Warm rides in winter",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Lemon Festival",
    steps: [
      "Tell us your dates, your hotel and the parades you want to see.",
      "We plan the drop-offs around the street closures near the Biovès gardens.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Direct transfer or hourly hire to cover the gardens, the parades and lunch.",
    ],
  },
  faq: [
    {
      q: "How do I book a private chauffeur during the Menton Lemon Festival?",
      a: "Book ahead: in February, Menton fills up and demand rises on parade weekends. Share your dates, your hotel and the corsos you want to see so we can secure a dedicated chauffeur.",
    },
    {
      q: "Where can the chauffeur drop me for the Biovès gardens and the parades?",
      a: "During the corsos, Menton's seafront and centre are closed to traffic. The chauffeur drops you at the nearest authorised point to the Biovès gardens or the stands, then collects you on the way out.",
    },
    {
      q: "Do you handle transfers from Nice airport and Monaco?",
      a: "Yes, with flight tracking and a name-board welcome. Nice Côte d'Azur airport is around thirty kilometres away and Monaco around ten: we adjust the pick-up time if your flight is delayed.",
    },
    {
      q: "Is parking difficult during the festival?",
      a: "Yes. On parade days, the streets close and Menton's car parks fill up. The chauffeur saves you the search for a space: he drops off, waits or returns at the agreed time.",
    },
    {
      q: "Is the service suitable for families?",
      a: "Absolutely. In winter, a chauffeur spares children and elders the wait outside and the connections: the ride is warm and stops as close as possible to the gardens and the parades.",
    },
    {
      q: "What vehicles do you offer?",
      a: "Saloons, SUVs and vans with a chauffeur depending on your group size, for a comfortable ride between the hotel, the gardens and the seafront.",
    },
    {
      q: "When does the Lemon Festival take place?",
      a: "It is held every February, over roughly three weeks, with corsos on several days. Book early for the weekends, which are the busiest.",
    },
  ],
  relatedLinks: [
    {
      label: "Menton chauffeur rates",
      href: "/en/chauffeur/menton",
    },
    {
      label: "Monaco chauffeur rates",
      href: "/en/chauffeur/monaco",
    },
    {
      label: "Book an airport transfer",
      href: "/en/chauffeur/airport",
    },
    {
      label: "Transfers across the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
  ],
  cta: {
    title: "Your chauffeur for the Menton Lemon Festival",
    text: "Airport transfer, Biovès gardens drop-off and parade access: book a dedicated chauffeur for your visit to Menton.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Menton Lemon Festival Chauffeur – car service | KS Rent",
    description:
      "Private chauffeur during the Menton Lemon Festival (Fête du Citron): Nice airport and Monaco transfers, Biovès gardens drop-off and parade access. Book now.",
    canonical: "/en/chauffeur/fete-du-citron-menton",
  },
};

export default feteDuCitronMentonEn;
